import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { callEdgeFunction } from "@/lib/edge-functions";
import { toTripTracking } from "./tracking-quality";
import type { SubmitMileageRequestInput, ApproveMileageRequestInput } from "@bednbody/types";

export function useCurrentMileageRate() {
  return useQuery({
    queryKey: ["mileage-current-rate"],
    queryFn: async () => {
      // current_mileage_rate() (0017) is service_role-only — it's the SNAPSHOT
      // source at trip creation, not a read meant for the admin UI. Interactive
      // roles already have a direct SELECT grant on mileage_rate_config (0009),
      // so read the live row the same way useSubmitMileageRequest's bracket
      // lookup does.
      const { data, error } = await supabase
        .from("mileage_rate_config")
        .select("rate_per_km")
        .is("effective_to", null)
        .order("effective_from", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data?.rate_per_km ?? null;
    },
    staleTime: undefined,
  });
}

export function useMileageRequests(status?: "pending" | "approved" | "rejected") {
  return useQuery({
    queryKey: ["mileage-requests", status ?? "all"],
    queryFn: async () => {
      let query = supabase
        .from("mileage_rate_change_requests")
        .select(
          "id, fuel_price, computed_bracket, suggested_rate, is_fast_track, requested_effective_date, status, notes, created_at, submitted_by, approved_by, approved_at",
        )
        .order("created_at", { ascending: false });
      if (status) query = query.eq("status", status);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    staleTime: undefined,
  });
}

/** Looks up last week's average fuel price to evaluate the fast-track spike rule. */
export function useLastWeekFuelPrice() {
  return useQuery({
    queryKey: ["fuel-price-last-week"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fuel_price_log")
        .select("week_of, avg_unleaded_price")
        .order("week_of", { ascending: false })
        .limit(1);
      if (error) throw error;
      return data?.[0]?.avg_unleaded_price ?? null;
    },
    staleTime: undefined,
  });
}

/**
 * Admin submits a rate change request. Bracket, fast-track flag, and the
 * suggested rate are all recomputed server-side by
 * submit_mileage_rate_request (migration 0022) — this is a preview-only
 * calculation on the client (packages/logic/mileage-preview.ts), never the
 * value that gets stored (money-math.md).
 */
export function useSubmitMileageRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SubmitMileageRequestInput) => {
      return callEdgeFunction("admin/mileage/requests", { data: input });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mileage-requests"] });
    },
  });
}

/**
 * Executive approval (Business Logic §8.4). approve_mileage_rate_request
 * (0022) does the status flip + the temporal mileage_rate_config swap +
 * the audit_log entry in ONE transaction — this used to be two separate
 * client-side writes with no transaction, and mileage_rate_config /
 * mileage_rate_change_requests only grant SELECT to interactive roles
 * anyway (0009), so those writes could never have succeeded.
 */
export function useApproveMileageRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { requestId: string } & ApproveMileageRequestInput) => {
      const { requestId, ...body } = input;
      return callEdgeFunction(`admin/mileage/requests/${requestId}/approve`, { data: body });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mileage-requests"] });
      queryClient.invalidateQueries({ queryKey: ["mileage-current-rate"] });
    },
  });
}

/**
 * Completed trips and whether their distance was actually measured — 0199.
 *
 * ⚠ `status = 'completed'` ONLY. The three diagnostic columns are written by
 * `end_trip`, so a running trip has nulls that mean "not finished", not "not
 * measured" — and `isMeasured` cannot tell those two apart. Filtering here is
 * what keeps the distinction honest.
 *
 * ⚠ NO HUB FILTER, DELIBERATELY. `/mileage` is admin/executive/superadmin and
 * `trips_admin_read` grants all rows; rider dispatch pay is not hub-scoped, so
 * scoping this would hide gapped trips from the only people who can act on the
 * Open Item behind them.
 */
export function useTripTrackingQuality(days = 30) {
  return useQuery({
    queryKey: ["trip-tracking-quality", days],
    queryFn: async () => {
      const since = new Date();
      since.setDate(since.getDate() - (days - 1));
      since.setHours(0, 0, 0, 0);

      // `staff` has one FK from trips (rider_id), but name it anyway — the
      // moment a second is added an unqualified embed becomes ambiguous.
      const { data, error } = await supabase
        .from("trips")
        .select(
          `id, total_km, straight_line_km, tracking_gaps, max_gap_seconds, ended_at,
           staff:staff!trips_rider_id_fkey ( full_name )`,
        )
        .eq("status", "completed")
        .gte("ended_at", since.toISOString())
        .order("ended_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []).map((row) => toTripTracking(row as Record<string, unknown>));
    },
    staleTime: 60_000,
  });
}
